import React from 'react';
import { motion } from 'framer-motion';
import { 
  Edit3, 
  Settings, 
  Award, 
  TrendingUp, 
  Flame, 
  Calendar,
  ChevronRight,
  Share,
  Bell,
  Shield,
  HelpCircle,
  LogOut
} from 'lucide-react';

export default function Profile() {
  const user = {
    name: 'Alex Johnson',
    username: '@alexeco',
    avatar: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop',
    level: 'Forest Guardian',
    ecoCoins: 1247,
    streak: 12,
    joinDate: 'January 2024',
    location: 'San Francisco, CA'
  };

  const stats = [
    { icon: Flame, label: 'Day Streak', value: '12', color: 'text-orange-600', bg: 'bg-orange-100' },
    { icon: TrendingUp, label: 'Level', value: 'Tree', color: 'text-emerald-600', bg: 'bg-emerald-100' },
    { icon: Award, label: 'Missions', value: '47', color: 'text-purple-600', bg: 'bg-purple-100' },
  ];

  const achievements = [
    { id: 1, title: 'First Steps', description: 'Complete your first mission', icon: '🌱', unlocked: true },
    { id: 2, title: 'Week Warrior', description: 'Complete missions for 7 days straight', icon: '🔥', unlocked: true },
    { id: 3, title: 'Eco Champion', description: 'Reach Forest Guardian level', icon: '🏆', unlocked: true },
    { id: 4, title: 'Community Leader', description: 'Organize 5 community events', icon: '👥', unlocked: false },
  ];

  const menuItems = [
    { icon: Edit3, label: 'Edit Profile', action: () => {} },
    { icon: Bell, label: 'Notifications', action: () => {} },
    { icon: Shield, label: 'Privacy & Security', action: () => {} },
    { icon: Share, label: 'Share App', action: () => {} },
    { icon: HelpCircle, label: 'Help & Support', action: () => {} },
    { icon: LogOut, label: 'Sign Out', action: () => {}, danger: true },
  ];

  return (
    <div className="h-full mobile-scroll custom-scrollbar">
      <div className="space-y-6 pb-6">
        {/* Profile Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-gradient-to-br from-emerald-500 to-teal-600 p-6 text-white"
        >
          <div className="flex items-center space-x-4 mb-6">
            <div className="relative">
              <img
                src={user.avatar}
                alt={user.name}
                className="w-20 h-20 rounded-3xl border-4 border-white/20"
              />
              <button className="absolute -bottom-2 -right-2 bg-white text-emerald-600 p-2 rounded-full shadow-lg">
                <Edit3 className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-1">{user.name}</h2>
              <p className="text-emerald-100 mb-2">{user.username}</p>
              <div className="flex items-center space-x-2">
                <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">
                  {user.level}
                </span>
                <span className="text-emerald-100 text-sm">• {user.location}</span>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-4">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.2, delay: 0.1 + index * 0.05 }}
                  className="bg-white/10 backdrop-blur-sm rounded-2xl p-3 text-center"
                >
                  <Icon className="h-6 w-6 mx-auto mb-2 text-white" />
                  <p className="text-xl font-bold">{stat.value}</p>
                  <p className="text-xs text-emerald-100">{stat.label}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* EcoCoins Balance */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="mx-4 bg-white rounded-2xl p-4 card-shadow"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Your Balance</p>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
                <span className="text-2xl font-bold text-gray-900">
                  {user.ecoCoins.toLocaleString()} EcoCoins
                </span>
              </div>
            </div>
            <button className="bg-emerald-500 text-white px-4 py-2 rounded-xl font-medium">
              Redeem
            </button>
          </div>
        </motion.div>

        {/* Achievements */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="mx-4"
        >
          <h3 className="text-xl font-bold text-gray-900 mb-4">Achievements</h3>
          <div className="grid grid-cols-2 gap-3">
            {achievements.map((achievement, index) => (
              <motion.div
                key={achievement.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.2, delay: 0.2 + index * 0.05 }}
                className={`bg-white rounded-2xl p-4 card-shadow ${
                  !achievement.unlocked ? 'opacity-50' : ''
                }`}
              >
                <div className="text-3xl mb-2">{achievement.icon}</div>
                <h4 className="font-semibold text-gray-900 mb-1">{achievement.title}</h4>
                <p className="text-xs text-gray-600">{achievement.description}</p>
                {achievement.unlocked && (
                  <div className="mt-2 text-xs text-emerald-600 font-medium">
                    ✓ Unlocked
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Menu Items */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
          className="mx-4 bg-white rounded-2xl card-shadow overflow-hidden"
        >
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <button
                key={index}
                onClick={item.action}
                className={`w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors ${
                  index !== menuItems.length - 1 ? 'border-b border-gray-100' : ''
                } ${item.danger ? 'text-red-600' : 'text-gray-900'}`}
              >
                <div className="flex items-center space-x-3">
                  <Icon className={`h-5 w-5 ${item.danger ? 'text-red-600' : 'text-gray-600'}`} />
                  <span className="font-medium">{item.label}</span>
                </div>
                <ChevronRight className={`h-5 w-5 ${item.danger ? 'text-red-400' : 'text-gray-400'}`} />
              </button>
            );
          })}
        </motion.div>

        {/* App Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
          className="mx-4 text-center"
        >
          <p className="text-sm text-gray-500 mb-2">
            Member since {user.joinDate}
          </p>
          <p className="text-xs text-gray-400">
            GreenStep v1.0.0 • Made with 💚 for the planet
          </p>
        </motion.div>
      </div>
    </div>
  );
}